/** Small pieces that show or pick an environment: the colour dot with the name, a select of the tenant's environments, and pills. */
import type { SelectHTMLAttributes } from 'react';
import { envColor, type Environment } from '../lib/env.ts';
import { Menu, cx } from '../design/primitives.tsx';
import { inputClass } from './ui.tsx';
import { CheckIcon, ChevronIcon, LockIcon } from './icons.tsx';

export function EnvDot({ color, className }: { color: string | null | undefined; className?: string }) {
  return <i className={cx('inline-block size-2 rounded-full shrink-0', className)} style={{ background: envColor(color) }} aria-hidden="true" />;
}

/** The environment as a dot and its name; an id the list does not know shows as it is. */
export function EnvLabel({ env, className }: { env: Environment | undefined; className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-2 min-w-0', className)}>
      <EnvDot color={env?.color} />
      <span className="truncate">{env?.name ?? 'Unknown'}</span>
    </span>
  );
}

/** A native select of the environments (keyboard and screen reader friendly), in the form style of the app (for forms). */
export function EnvSelect({ envs, value, onChange, className, ...rest }: {
  envs: Environment[]; value: string; onChange: (id: string) => void;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  return (
    <select {...rest} value={value} onChange={(e) => onChange(e.target.value)} className={cx(inputClass, 'cursor-pointer', className)}>
      {envs.map((e) => <option key={e.id} value={e.id}>{e.name}{e.protected ? ' (protected)' : ''}</option>)}
    </select>
  );
}

/** Up to this many environments show as pills; more as a menu. */
export const ENV_PILLS_MAX = 5;

/**
 * Picks the environment a page looks at: a pill per environment (colour dot and name, a lock on a protected one) in a
 * quiet track, the current one lifted; with more than ENV_PILLS_MAX a menu like the header's environment switcher.
 */
export function EnvPicker({ envs, value, onChange, className }: { envs: Environment[]; value: string; onChange: (id: string) => void; className?: string }) {
  const current = envs.find((e) => e.id === value);
  const lock = (e: Environment) => e.protected && <><LockIcon size={11} className="shrink-0 text-n7" /><span className="sr-only">(protected)</span></>;
  if (envs.length > ENV_PILLS_MAX) {
    return (
      <Menu.Root>
        <Menu.Trigger aria-label={`Environment: ${current?.name ?? ''}`}
          className={cx('inline-flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-full bg-n2 text-n10 text-small font-medium shadow-node cursor-pointer border-0 outline-none min-w-0 max-w-[220px] focus-visible:ring-2 focus-visible:ring-accent', className)}>
          <EnvDot color={current?.color} />
          <span className="truncate">{current?.name ?? 'Environment'}</span>
          {current && lock(current)}
          <ChevronIcon size={14} className="text-n7 shrink-0" />
        </Menu.Trigger>
        <Menu.Content align="end" className="min-w-64">
          <Menu.Label>Environment</Menu.Label>
          {envs.map((e) => (
            <Menu.Item key={e.id} onSelect={() => { if (e.id !== value) onChange(e.id); }} aria-current={e.id === value ? 'true' : undefined}>
              <span className="flex items-center gap-2.5 w-full min-w-0">
                <EnvDot color={e.color} />
                <span className="truncate flex-1">{e.name}</span>
                {e.protected && <span className="text-micro text-n7 whitespace-nowrap">protected</span>}
                <CheckIcon size={14} className={cx('shrink-0 text-accent', e.id !== value && 'invisible')} />
              </span>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Root>
    );
  }
  return (
    <div role="group" aria-label="Environment" className={cx('flex items-center gap-0.5 p-0.5 rounded-full bg-n3 w-fit max-w-full flex-wrap', className)}>
      {envs.map((e) => (
        <button key={e.id} type="button" aria-pressed={e.id === value} data-env={e.id} onClick={() => { if (e.id !== value) onChange(e.id); }}
          title={e.protected ? `${e.name} (protected)` : e.name}
          className={cx('inline-flex items-center gap-1.5 h-7 px-3 rounded-full border-0 cursor-pointer text-small whitespace-nowrap min-w-0 transition-colors duration-[var(--t-micro)]',
            e.id === value ? 'bg-n1 text-n10 font-medium shadow-[0_1px_2px_rgb(0_0_0/0.1)]' : 'bg-transparent text-n8 hover:text-n10')}>
          <EnvDot color={e.color} />
          <span className="truncate max-w-[160px]">{e.name}</span>
          {lock(e)}
        </button>
      ))}
    </div>
  );
}
